import React, { useEffect, useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { CheckCircle2, Home, Mail, KeyRound, MessageSquare, Copy, Send } from "lucide-react";
import { Section } from "../components/UIBits";
import { TOURNAMENT } from "../lib/constants";
import { useT } from "../lib/i18n";

const INTERAC_EMAIL = "defiultimedek@gmail.com";
const INTERAC_ANSWER = "hockey";

export default function InscriptionSuccessPage() {
    const { t } = useT();
    const loc = useLocation();
    const [params] = useSearchParams();
    const initial = loc.state?.registration;
    const [reg, setReg] = useState(initial);
    const [copied, setCopied] = useState("");

    useEffect(() => {
        if (!reg && params.get("id")) { /* no public single-fetch endpoint */ }
    }, [reg, params]);

    const copy = async (text, label) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopied(label);
            setTimeout(() => setCopied(""), 2000);
        } catch { /* ignore */ }
    };

    const interacMessage = reg ? `${reg.team_name} - ${reg.category}` : "";

    return (
        <div data-testid="inscription-success-page">
            <Helmet><title>{t("inscription.success_title")} — Défi Ultime</title></Helmet>
            <Section>
                <div className="max-w-3xl mx-auto">
                    <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="glass-card p-8 mb-6 border-t-4 border-[#7FD93C]">
                        <CheckCircle2 size={56} className="text-[#7FD93C] mx-auto mb-3" />
                        <div className="text-center">
                            <div className="label-mini mb-2" style={{ color: "#7FD93C" }}>{t("inscription.success_title")}</div>
                            <h1 className="font-display text-4xl uppercase tracking-tight mb-2">{reg ? `${reg.manager_name}` : ""}</h1>
                            <p className="text-white/70">{t("inscription.success_body")}</p>
                        </div>
                    </motion.div>

                    {reg && (
                        <div className="glass-card p-6 mb-6 border-l-4 border-[#4FE0D5]" data-testid="inscription-receipt">
                            <h2 className="font-display text-2xl uppercase tracking-widest text-[#4FE0D5] mb-4">{t("inscription.title_pre")} {t("inscription.title_accent")}</h2>
                            <div className="grid gap-2 text-sm">
                                <div className="flex justify-between border-b border-white/10 pb-2"><span className="text-white/60">{t("inscription.team_name")}</span><span className="font-bold">{reg.team_name}</span></div>
                                <div className="flex justify-between border-b border-white/10 pb-2"><span className="text-white/60">{t("inscription.category")}</span><span>{reg.category}</span></div>
                                <div className="flex justify-between border-b border-white/10 pb-2"><span className="text-white/60">{t("inscription.city")}</span><span>{reg.city}</span></div>
                                <div className="flex justify-between border-b border-white/10 pb-2"><span className="text-white/60">{t("inscription.manager")}</span><span>{reg.manager_name}</span></div>
                                <div className="flex justify-between border-b border-white/10 pb-2"><span className="text-white/60">{t("inscription.email")}</span><span>{reg.email}</span></div>
                                <div className="flex justify-between"><span className="text-white/60">{t("inscription.phone")}</span><span>{reg.phone}</span></div>
                            </div>
                        </div>
                    )}

                    <div className="glass-card p-6 mb-6 border-l-4 border-[#7FD93C]" data-testid="interac-instructions">
                        <div className="flex items-center gap-2 mb-4">
                            <Send className="text-[#7FD93C]" size={22} />
                            <h2 className="font-display text-2xl uppercase tracking-widest">{t("inscription.interac_title")}</h2>
                        </div>
                        <p className="text-sm text-white/70 mb-5">{t("inscription.interac_lead")}</p>

                        <div className="grid gap-3 mb-6">
                            <Field icon={<Mail size={18} />} label={t("inscription.interac_email_label")} value={INTERAC_EMAIL} onCopy={() => copy(INTERAC_EMAIL, "email")} copied={copied === "email"} testid="copy-email" t={t} />
                            <Field icon={<KeyRound size={18} />} label={t("inscription.interac_answer_label")} value={INTERAC_ANSWER} onCopy={() => copy(INTERAC_ANSWER, "answer")} copied={copied === "answer"} testid="copy-answer" t={t} />
                            {reg && <Field icon={<MessageSquare size={18} />} label={t("inscription.interac_message_label")} value={interacMessage} onCopy={() => copy(interacMessage, "message")} copied={copied === "message"} testid="copy-message" t={t} />}
                            <div className="bg-black/40 p-4 flex items-center justify-between border-l-2 border-[#7FD93C]">
                                <div>
                                    <div className="label-mini mb-1">{t("inscription.total")}</div>
                                    <div className="font-display text-4xl text-[#7FD93C]">{TOURNAMENT.registrationFee} $ <span className="text-base text-white/60">{TOURNAMENT.currency}</span></div>
                                </div>
                            </div>
                        </div>

                        <div className="bg-[#4FE0D5]/10 border-l-2 border-[#4FE0D5] p-4">
                            <div className="text-xs text-white/80 leading-relaxed">{t("inscription.interac_safe")}</div>
                        </div>
                    </div>

                    <div className="flex flex-wrap gap-3 justify-center">
                        <Link to="/" className="btn-outline" data-testid="link-home"><Home size={14} /> {t("common.back")}</Link>
                        <Link to="/inscription" className="btn-ghost" data-testid="link-new-registration">{t("home.cta_inscrire")}</Link>
                    </div>
                </div>
            </Section>
        </div>
    );
}

function Field({ icon, label, value, onCopy, copied, testid, t }) {
    return (
        <div className="bg-black/40 p-4 flex items-center gap-3">
            <div className="text-[#7FD93C] flex-shrink-0">{icon}</div>
            <div className="flex-1 min-w-0">
                <div className="label-mini mb-1">{label}</div>
                <div className="font-mono text-sm break-all">{value}</div>
            </div>
            <button onClick={onCopy} className="btn-ghost flex items-center gap-1 flex-shrink-0" data-testid={testid}>
                <Copy size={12} /> {copied ? t("common.copied") : t("common.copy")}
            </button>
        </div>
    );
}
